/**
 * Actions de jet d'un personnage VtM : lance avec le moteur, publie dans le chat et répercute
 * les effets sur la fiche (Soif, Volonté, Humanité).
 */
import {uid} from 'uid';
import {
  buildPool,
  rollPool,
  willpowerReroll,
  rouseCheck,
  rouseChecks,
  mendAmount,
  remorseCheck,
  frenzyCheck,
} from './dice';
import {applyDamage, getRemaining, getWillpowerMax, mendTrack} from './character';
import {ROLL_TYPES, createRollRecord} from './rolls';
import {pushRoll, updateRoll} from './rollRepository';
import {updateSheet} from './characterRepository';
import {changedFields} from './progression';

const resultFields = (roll) => ({
  regular: roll.regular,
  hunger: roll.hunger,
  difficulty: roll.difficulty ?? null,
  successes: roll.successes,
  outcome: roll.outcome ?? null,
  margin: roll.margin ?? null,
  messyIfWin: Boolean(roll.messyIfWin),
  bestialIfFail: Boolean(roll.bestialIfFail),
  autoFailure: Boolean(roll.autoFailure),
});

export const useVtmRollActions = ({character, campaign, user}) => {
  const sheet = character.vtm;
  const campaignUid = campaign.uid || character.idCampaign;

  const publish = async (type, fields) => {
    const record = createRollRecord({id: uid(), type, character, userUid: user.uid, ...fields});
    const key = await pushRoll(campaignUid, record);
    return {key, record};
  };

  /** Tests d'exaltation d'un pouvoir (relançables selon la Puissance du Sang et le niveau du pouvoir). */
  const powerRouse = (power, hunger) => rouseChecks({
    count: power?.rouse || 0,
    hunger,
    bloodPotency: sheet.bloodPotency,
    disciplineLevel: power?.level,
  });

  /**
   * Jet de réserve. Les tests d'exaltation (pouvoir, puis Coup de sang) passent d'abord : si la Soif monte, le jet en tient compte.
   * @param {Object} [options.power] pouvoir utilisé (POWERS), pour ses tests d'exaltation
   * @returns {Promise<{key, record, roll}>} `roll` (résultat moteur) sert à la relance de Volonté
   */
  const rollDicePool = async ({base, label, specialty, bloodSurge, impaired, modifier, difficulty, hidden, power, requestId}) => {
    const pool = buildPool({
      base,
      specialty,
      bloodSurge: bloodSurge ? sheet.bloodPotency : undefined,
      impaired,
      modifiers: modifier ? [{label: 'modifier', value: modifier}] : [],
    });
    const powerChecks = powerRouse(power, sheet.hunger);
    const rouse = pool.requiresRouseCheck ? rouseCheck({hunger: powerChecks.newHunger, bloodPotency: sheet.bloodPotency}) : null;
    const hunger = rouse ? rouse.newHunger : powerChecks.newHunger;
    const roll = rollPool({size: pool.size, hunger, difficulty});
    const {key, record} = await publish(ROLL_TYPES.POOL, {
      label,
      hidden,
      breakdown: pool.breakdown,
      ...resultFields(roll),
      rouse,
      powerId: power?.id,
      powerRouses: powerChecks.checks,
      requestId,
    });
    if (hunger !== sheet.hunger) {
      await updateSheet(character.uid, {hunger});
    }
    return {key, record, roll};
  };

  /** Pouvoir sans jet : on fait seulement ses tests d'exaltation et on l'annonce dans le chat. */
  const activatePower = async ({power, label, hidden = false}) => {
    const {checks, newHunger} = powerRouse(power, sheet.hunger);
    const {key, record} = await publish(ROLL_TYPES.POWER, {label, hidden, powerId: power.id, powerRouses: checks});
    if (newHunger !== sheet.hunger) {
      await updateSheet(character.uid, {hunger: newHunger});
    }
    return {key, record};
  };

  /** Soin : un test d'exaltation soigne des dégâts superficiels de Santé, que le test réussisse ou non. */
  const mend = async ({hidden = false} = {}) => {
    const rouse = rouseCheck({hunger: sheet.hunger, bloodPotency: sheet.bloodPotency});
    const healed = Math.min(sheet.health.superficial, mendAmount(sheet.bloodPotency));
    await publish(ROLL_TYPES.MEND, {hidden, rouse, mend: {healed}});
    await updateSheet(character.uid, {health: mendTrack(sheet.health, healed), hunger: rouse.newHunger});
    return {rouse, healed};
  };

  /** Le personnage se nourrit : la Soif descend à la valeur choisie. */
  const feed = async ({newHunger, note = '', hidden = false}) => {
    await publish(ROLL_TYPES.FEED, {hidden, feed: {from: sheet.hunger, to: newHunger, note: note.trim()}});
    await updateSheet(character.uid, {hunger: newHunger});
  };

  /** Relance de Volonté : met à jour le jet dans le chat et coche 1 dégât superficiel de Volonté. */
  const rerollWithWillpower = async ({key, record, roll}, indexes) => {
    const rerolled = willpowerReroll(roll, indexes);
    const patch = {
      ...resultFields(rerolled),
      willpowerRerolled: true,
      previousRegular: rerolled.previousRegular,
      rerolledIndexes: rerolled.rerolledIndexes,
    };
    await updateRoll(campaignUid, key, patch);
    await updateSheet(character.uid, {
      willpower: applyDamage(sheet.willpower, getWillpowerMax(sheet), {superficial: rerolled.willpowerCost}),
    });
    return {key, record: {...record, ...patch}, roll: rerolled};
  };

  const rollRouse = async ({hidden = false} = {}) => {
    const rouse = rouseCheck({hunger: sheet.hunger, bloodPotency: sheet.bloodPotency});
    await publish(ROLL_TYPES.ROUSE, {hidden, rouse});
    if (rouse.newHunger !== sheet.hunger) {
      await updateSheet(character.uid, {hunger: rouse.newHunger});
    }
    return rouse;
  };

  const rollRemorse = async ({hidden = false} = {}) => {
    const remorse = remorseCheck({humanity: sheet.humanity, stains: sheet.stains});
    if (!remorse.required) return remorse;
    await publish(ROLL_TYPES.REMORSE, {hidden, remorse});
    await updateSheet(character.uid, {humanity: remorse.newHumanity, stains: remorse.newStains});
    return remorse;
  };

  const rollFrenzy = async ({difficulty, hidden = false, requestId}) => {
    const result = frenzyCheck({
      willpower: getRemaining(sheet.willpower, getWillpowerMax(sheet)),
      humanity: sheet.humanity,
      difficulty,
    });
    const {key, record} = await publish(ROLL_TYPES.FRENZY, {hidden, requestId, ...resultFields(result)});
    return {key, record, result};
  };

  /** Achat d'XP préparé par preparePurchase : met à jour la fiche et l'annonce dans le chat. */
  const buyWithXp = async (prepared, summary) => {
    await updateSheet(character.uid, changedFields(sheet, prepared.sheet));
    await publish(ROLL_TYPES.XP, {xp: {summary, cost: prepared.cost}});
  };

  return {rollDicePool, activatePower, rerollWithWillpower, rollRouse, rollRemorse, rollFrenzy, mend, feed, buyWithXp};
};
